import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { AdminAccessLevel } from '../../types/admin/adminNavigation.types';
import { hasAccessToLevel } from '../../utils/adminAccessUtils';
import { getUserAccessFlags } from '../../utils/authUtils';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Props for AdminRouteGuard component
 */
interface AdminRouteGuardProps {
  /**
   * Required access level for the guarded routes
   * - ADMIN: Accessible by isVerityAdmin OR isDeveloper
   * - DEVELOPER: Accessible only by isDeveloper
   * @default AdminAccessLevel.ADMIN
   */
  requiredAccessLevel?: AdminAccessLevel;
}

/**
 * AdminRouteGuard component
 *
 * Guards admin routes based on user access level.
 * Redirects users without required access to the home page.
 *
 * Access levels:
 * - ADMIN: isVerityAdmin OR isDeveloper can access
 * - DEVELOPER: Only isDeveloper can access
 *
 * @example
 * // For routes accessible by both admins and developers
 * <Route element={<AdminRouteGuard requiredAccessLevel={AdminAccessLevel.ADMIN} />}>
 *   <Route path="/access-management" element={<AccessManagement />} />
 * </Route>
 *
 * @example
 * // For routes accessible only by developers
 * <Route element={<AdminRouteGuard requiredAccessLevel={AdminAccessLevel.DEVELOPER} />}>
 *   <Route path="/admin/reports-configuration" element={<ReportConfiguration />} />
 * </Route>
 */
const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({ requiredAccessLevel = AdminAccessLevel.ADMIN }) => {
  const { user, isAuthenticated } = useAuth();
  const { isInitiallyDeveloper } = getUserAccessFlags(user);
  const { isTechAdmin, isVerityAdmin } = usePermissions();

  // If user is not authenticated, redirect to home page
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  // Check if user has required access level
  // Pass isInitiallyDeveloper so developers with DB-precedence override
  // can still access admin routes (e.g., Access Management page)
  const hasAccess = hasAccessToLevel(requiredAccessLevel, isVerityAdmin, isTechAdmin, isInitiallyDeveloper);

  // If user doesn't have required access, redirect to home page
  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  // If user has required access, render the child routes
  return <Outlet />;
};

export default AdminRouteGuard;
